/*
## Summary
Messages cache composable. Manages message and conversation data caching.

## Responsibilities
- Cache and expose messages and conversations
- Provide reactive access to message data
- Handle message history caching
- Manage conversation list caching
- Track loading states

## Out of Scope
- Message sending (delegated to MessageService)
- Real-time message streaming (delegated to RealtimeService)
- Message encryption/decryption
- Message search indexing
- Notification generation

## Dependencies
- Vue 3 reactivity
- Message service
- Message and conversation types
- Cache management

## Notes
Caches to minimize repeated message list fetches.
*/
import { ref, unref, type Ref } from 'vue'
import type { AgencyUser, ChatChannelWithDetails, ChatMessage } from '@/types'
import { useAppCache } from './useAppCache'

export const CHANNEL_LIST_PAGE_SIZE = 30
const AGENCY_USERS_TTL_MS = 2 * 60_000

/** Shape stored in cache for the channel list. Includes pagination state. */
export type ChannelPageCache = {
  channels: ChatChannelWithDetails[]
  hasMore: boolean
}

const KEYS = {
  channels: (userId: string, agencyId: string) => `messages:channels:${userId}:${agencyId}`,
  messages: (channelId: string) => `messages:messages:${channelId}`,
  agencyUsers: (agencyId: string) => `messages:agency-users:${agencyId}`,
}

// ─── Low-level cache helpers (used by mobile page that owns its own state) ───

export function useMessagesCache() {
  const cache = useAppCache()

  function getCachedChannels(userId: string, agencyId: string): ChatChannelWithDetails[] | undefined {
    if (!userId || !agencyId) return undefined
    const entry = cache.get<ChannelPageCache>(KEYS.channels(userId, agencyId))
    return entry?.channels
  }

  function setCachedChannels(userId: string, agencyId: string, channels: ChatChannelWithDetails[], hasMore = false): void {
    if (!userId || !agencyId) return
    cache.set(KEYS.channels(userId, agencyId), { channels, hasMore } satisfies ChannelPageCache)
  }

  function updateChannelInCache(
    userId: string,
    agencyId: string,
    updater: (channels: ChatChannelWithDetails[]) => ChatChannelWithDetails[],
  ): void {
    if (!userId || !agencyId) return
    const key = KEYS.channels(userId, agencyId)
    const cached = cache.get<ChannelPageCache>(key)
    if (cached !== undefined) {
      cache.set(key, { ...cached, channels: updater([...cached.channels]) })
    }
  }

  function invalidateChannels(userId: string, agencyId: string): void {
    if (!userId || !agencyId) return
    cache.invalidate(KEYS.channels(userId, agencyId))
  }

  function getCachedMessages(channelId: string): ChatMessage[] | undefined {
    if (!channelId) return undefined
    return cache.get<ChatMessage[]>(KEYS.messages(channelId))
  }

  function setCachedMessages(channelId: string, messages: ChatMessage[]): void {
    if (!channelId) return
    cache.set(KEYS.messages(channelId), messages)
  }

  function invalidateMessages(channelId: string): void {
    if (!channelId) return
    cache.invalidate(KEYS.messages(channelId))
  }

  function invalidateAll(): void {
    cache.invalidatePattern('messages:')
  }

  return {
    getCachedChannels,
    setCachedChannels,
    updateChannelInCache,
    invalidateChannels,
    getCachedMessages,
    setCachedMessages,
    invalidateMessages,
    invalidateAll,
  }
}

// ─── Paginated channels SWR ──────────────────────────────────────────────────

/**
 * SWR composable for a user's paginated channel list.
 *
 * - data starts as null (cold miss) or the cached list (instant, no flash).
 * - isLoading is true ONLY on a cold miss (nothing in cache yet).
 * - loadMore() appends the next page and updates the cache.
 * - reload(true) forces a fresh first page (pull-to-refresh / toolbar).
 *
 * @param fetcher - fetches a page: (offset) => Promise<{ channels, hasMore }>
 */
export function useChannelsSWR(
  userId: Ref<string> | string,
  agencyId: Ref<string> | string,
  fetcher: (offset: number) => Promise<{ channels: ChatChannelWithDetails[]; hasMore: boolean }>,
) {
  const cache = useAppCache()
  const data = ref<ChatChannelWithDetails[] | null>(null)
  const hasMore = ref(false)
  const isLoading = ref(false)
  const isRevalidating = ref(false)
  const isLoadingMore = ref(false)
  const error = ref<string | null>(null)

  function _key() {
    return KEYS.channels(unref(userId), unref(agencyId))
  }

  function _hydrateFromCache(): ChannelPageCache | undefined {
    const uid = unref(userId)
    const aid = unref(agencyId)
    if (!uid || !aid) return undefined
    return cache.get<ChannelPageCache>(_key())
  }

  /** Fetch first page and replace cache + reactive state. */
  async function _fetchFirstPage(force: boolean): Promise<void> {
    const uid = unref(userId)
    const aid = unref(agencyId)
    if (!uid || !aid) return

    // getOrSWR with no TTL: on a no-TTL cache entry peek never marks isStale, so
    // it effectively behaves as a fresh hit and skips the background fetch unless forced.
    const result = await cache.getOrSWR<ChannelPageCache>(
      _key(),
      async () => {
        const page = await fetcher(0)
        return page
      },
      { force },
    )

    data.value = result.value.channels
    hasMore.value = result.value.hasMore

    if (result.revalidating) {
      isRevalidating.value = true
      result.revalidating
        .then((fresh) => {
          data.value = fresh.channels
          hasMore.value = fresh.hasMore
        })
        .catch((e) => {
          error.value = e instanceof Error ? e.message : String(e)
        })
        .finally(() => {
          isRevalidating.value = false
        })
    }
  }

  async function load(force = false): Promise<void> {
    const uid = unref(userId)
    const aid = unref(agencyId)
    if (!uid || !aid) return
    error.value = null

    try {
      await _fetchFirstPage(force)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      isLoading.value = false
    }
  }

  /** Call on mount — shows loading spinner only on cold miss; hydrates from cache sync otherwise. */
  function startLoad(): void {
    const cached = _hydrateFromCache()
    if (cached === undefined) {
      isLoading.value = true
    } else {
      // Sync hydrate: no flash between mount and async load() resolution.
      data.value = cached.channels
      hasMore.value = cached.hasMore
    }
    void load()
  }

  /** Append the next page (does NOT replace current list). */
  async function loadMore(): Promise<void> {
    if (isLoadingMore.value || !hasMore.value) return
    const uid = unref(userId)
    const aid = unref(agencyId)
    if (!uid || !aid) return

    isLoadingMore.value = true
    error.value = null
    try {
      const offset = (data.value ?? []).length
      const page = await fetcher(offset)
      const merged = [...(data.value ?? []), ...page.channels]
      data.value = merged
      hasMore.value = page.hasMore
      cache.set(_key(), { channels: merged, hasMore: page.hasMore } satisfies ChannelPageCache)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      isLoadingMore.value = false
    }
  }

  /** Patch the reactive list and update the cache (used by Realtime callbacks). */
  function patchChannel(
    uid: string,
    aid: string,
    updater: (channels: ChatChannelWithDetails[]) => ChatChannelWithDetails[],
  ): void {
    if (!data.value) return
    const updated = updater(data.value.slice())
    data.value = updated
    const key = KEYS.channels(uid, aid)
    const current = cache.get<ChannelPageCache>(key)
    cache.set(key, { channels: updated, hasMore: current?.hasMore ?? hasMore.value })
  }

  return {
    data,
    hasMore,
    isLoading,
    isRevalidating,
    isLoadingMore,
    error,
    load,
    startLoad,
    loadMore,
    reload: () => load(true),
    patchChannel,
  }
}

// ─── Per-channel messages SWR ────────────────────────────────────────────────

/**
 * SWR composable for a single channel's message page.
 *
 * - data starts null (cold miss) or the cached page (instant, no spinner).
 * - isLoading is true ONLY on cold miss.
 * - Realtime appends are handled by appendMessage().
 */
export function useMessagesSWR(
  channelId: Ref<string | null> | string | null,
  fetcher: (channelId: string, offset: number, limit: number) => Promise<ChatMessage[]>,
) {
  const cache = useAppCache()
  const PAGE_SIZE = 50
  const data = ref<ChatMessage[] | null>(null)
  const hasMore = ref(true)
  const offset = ref(0)
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const error = ref<string | null>(null)

  function _key(cid: string) {
    return KEYS.messages(cid)
  }

  async function load(reset: boolean, force = false): Promise<void> {
    const cid = unref(channelId)
    if (!cid) return
    error.value = null

    if (reset) {
      const cached = cache.get<ChatMessage[]>(_key(cid))
      if (cached && !force) {
        // Sync hydrate — instant display from cache
        data.value = cached
        offset.value = cached.length
        hasMore.value = cached.length >= PAGE_SIZE
        // Serve cache; skip network (no TTL = always fresh for Realtime-patched data)
        isLoading.value = false
        return
      }
      isLoading.value = !force
    } else {
      isLoadingMore.value = true
    }

    try {
      const fetchOffset = reset ? 0 : offset.value
      const page = await fetcher(cid, fetchOffset, PAGE_SIZE)

      if (page.length < PAGE_SIZE) hasMore.value = false

      if (reset) {
        data.value = page
        cache.set(_key(cid), page)
        offset.value = page.length
      } else {
        data.value = [...page, ...(data.value ?? [])]
        offset.value += page.length
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      isLoading.value = false
      isLoadingMore.value = false
    }
  }

  /** Call on channel-change — hydrates instantly, sets loading only on cold miss. */
  function startLoad(): void {
    const cid = unref(channelId)
    if (!cid) return
    const cached = cache.get<ChatMessage[]>(_key(cid))
    if (cached === undefined) {
      isLoading.value = true
    } else {
      data.value = cached
      offset.value = cached.length
      hasMore.value = cached.length >= PAGE_SIZE
    }
    void load(true)
  }

  function appendMessage(message: ChatMessage): void {
    if (!data.value) {
      data.value = [message]
    } else if (!data.value.some((m) => m.id === message.id)) {
      data.value = [...data.value, message]
    }
    // Update cache with the appended message
    const cid = unref(channelId)
    if (cid && data.value) {
      cache.set(_key(cid), data.value)
    }
  }

  function reset(): void {
    data.value = null
    hasMore.value = true
    offset.value = 0
    error.value = null
    isLoading.value = false
    isLoadingMore.value = false
  }

  return {
    data,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    load,
    startLoad,
    loadMore: () => load(false),
    reload: () => load(true, true),
    appendMessage,
    reset,
  }
}

// ─── Agency users SWR (for sender-name resolution) ───────────────────────────

/**
 * SWR composable for the flat list of agency users used to resolve sender names.
 * Cached with a 2-minute TTL so names don't reset on tab navigation.
 */
export function useAgencyUsersSWR(
  agencyId: Ref<string> | string,
  fetcher: () => Promise<AgencyUser[]>,
) {
  const cache = useAppCache()
  const data = ref<AgencyUser[] | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  async function load(force = false): Promise<void> {
    const aid = unref(agencyId)
    if (!aid) return
    error.value = null

    try {
      const result = await cache.getOrSWR<AgencyUser[]>(
        KEYS.agencyUsers(aid),
        fetcher,
        { ttlMs: AGENCY_USERS_TTL_MS, force },
      )
      data.value = result.value
      if (result.revalidating) {
        result.revalidating
          .then((fresh) => { data.value = fresh })
          .catch((e) => { error.value = e instanceof Error ? e.message : String(e) })
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      isLoading.value = false
    }
  }

  function startLoad(): void {
    const aid = unref(agencyId)
    if (aid) {
      const cached = cache.get<AgencyUser[]>(KEYS.agencyUsers(aid))
      if (cached === undefined) {
        isLoading.value = true
      } else {
        data.value = cached
      }
    }
    void load()
  }

  return {
    data,
    isLoading,
    error,
    startLoad,
    reload: () => load(true),
  }
}
